// Cross-surface sharing used by product pages, Style the Look, and the studio.
//
// Where the browser supports it (mostly mobile) we hand the OS share sheet the
// actual image FILE plus a caption + link — this is what lets a customer post
// straight to an Instagram feed post or Story, or to WhatsApp Status, with the
// picture attached. Instagram has no public "pre-fill a post" web URL, so the
// share-sheet-with-image route is the only reliable way to get the image there.
//
// On desktop (no Web Share API) we fall back to opening WhatsApp with the
// caption and copying the caption to the clipboard so it can be pasted into
// Instagram manually.

// Fetch an image URL into a File so it can ride along in navigator.share().
async function urlToFile(url) {
  try {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!blob.type.startsWith('image/')) return null;
    const ext = (blob.type.split('/')[1] || 'jpg').replace('jpeg', 'jpg');
    return new File([blob], `bikini-world.${ext}`, { type: blob.type });
  } catch {
    return null;
  }
}

// Build a caption from a title, optional description and a link.
export function buildCaption({ title, description, url, price }) {
  return [
    title ? `🛍️ ${title}` : '',
    price ? `💰 ${price}` : '',
    description ? `\n${String(description).slice(0, 300)}` : '',
    url ? `\n🛒 Shop now: ${url}` : '',
  ].filter(Boolean).join('\n');
}

// Returns one of: 'shared-file' | 'shared-text' | 'cancelled' | 'copied'
export async function shareContent({ title, text, url, imageUrl }) {
  const caption = [text, url].filter(Boolean).join('\n');

  // 1. Native share WITH the image file (best for Instagram / Stories / Status).
  if (imageUrl && typeof navigator !== 'undefined' && navigator.canShare) {
    const file = await urlToFile(imageUrl);
    if (file && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ title, text: caption, files: [file] });
        return 'shared-file';
      } catch (e) {
        if (e?.name === 'AbortError') return 'cancelled';
      }
    }
  }

  // 2. Native share of caption + link (mobile without file support).
  if (typeof navigator !== 'undefined' && navigator.share) {
    try {
      await navigator.share({ title, text, url });
      return 'shared-text';
    } catch (e) {
      if (e?.name === 'AbortError') return 'cancelled';
    }
  }

  // 3. Desktop fallback: copy the caption and open WhatsApp web.
  try { await navigator.clipboard.writeText(caption); } catch { /* clipboard may be blocked */ }
  window.open(`https://wa.me/?text=${encodeURIComponent(caption)}`, '_blank', 'noopener,noreferrer');
  return 'copied';
}

// Trigger a browser download of an image (handy for manual Instagram posting).
export async function downloadImage(url, fileName = 'bikini-world.jpg') {
  try {
    const res = await fetch(url, { mode: 'cors' });
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
    return true;
  } catch {
    // Last resort: open the image in a new tab so it can be saved manually.
    window.open(url, '_blank', 'noopener,noreferrer');
    return false;
  }
}
